import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'app-pwa-install',
    imports: [CommonModule],
    template: `
    <!-- AUDIT FIX (2026-06-16): removed [@slideUp] — no animations provider/metadata was registered, so it
         errored at render. The .pwa-banner CSS @keyframes already animates the entrance. -->
    <div class="pwa-banner" *ngIf="showBanner">
      <div class="pwa-content">
        <div class="pwa-icon"></div>
        <div class="pwa-text">
          <strong>Install MediTrack</strong>
          <span>Add to home screen for quick access</span>
        </div>
      </div>
      <div class="pwa-actions">
        <button class="pwa-install-btn" (click)="install()">Install</button>
        <button class="pwa-dismiss-btn" (click)="dismiss()">Not now</button>
      </div>
    </div>
  `,
    changeDetection: ChangeDetectionStrategy.Eager,
    styles: [`
    .pwa-banner {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      background: linear-gradient(135deg, #0E6B63, #0B5A53);
      color: white;
      padding: 14px 20px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      z-index: 10000;
      box-shadow: 0 -4px 20px rgba(0,0,0,0.15);
      animation: slideUp 0.3s ease-out;
      /* rendered outside the app shell, so it inherited the browser serif — use the brand font */
      font-family: var(--mt-font, 'Plus Jakarta Sans', system-ui, sans-serif);
    }
    .pwa-icon:empty { display: none; }   /* emoji was removed in the redesign; drop the empty slot */
    /* a global phone rule pads every *-content element by the 76px bottom-nav clearance,
       which opened a big empty band inside this card */
    .pwa-banner .pwa-content { padding: 0 !important; }
    @keyframes slideUp {
      from { transform: translateY(100%); }
      to { transform: translateY(0); }
    }
    .pwa-content {
      display: flex;
      align-items: center;
      gap: 12px;
      flex: 1;
    }
    .pwa-icon {
      font-size: 28px;
      flex-shrink: 0;
    }
    .pwa-text {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
    .pwa-text strong {
      font-size: 14px;
      font-weight: 700;
    }
    .pwa-text span {
      font-size: 12px;
      opacity: 0.85;
    }
    .pwa-actions {
      display: flex;
      gap: 8px;
      flex-shrink: 0;
    }
    .pwa-install-btn {
      background: white;
      color: #0E6B63;
      border: none;
      border-radius: 8px;
      padding: 8px 18px;
      font-size: 13px;
      font-weight: 700;
      cursor: pointer;
      font-family: inherit;
      transition: transform 0.1s;
    }
    .pwa-install-btn:hover { transform: scale(1.03); }
    .pwa-dismiss-btn {
      background: transparent;
      color: rgba(255,255,255,0.8);
      border: 1px solid rgba(255,255,255,0.3);
      border-radius: 8px;
      padding: 8px 12px;
      font-size: 12px;
      cursor: pointer;
      font-family: inherit;
    }
    .pwa-dismiss-btn:hover { color: white; border-color: rgba(255,255,255,0.6); }

    /* Phones: a compact floating card ABOVE the 69px bottom nav (it used to cover the nav and
       every pinned Save/Confirm bar until dismissed). */
    @media (max-width: 767px) {
      .pwa-banner {
        left: 12px; right: 12px; bottom: calc(69px + 10px + env(safe-area-inset-bottom, 0px));
        border-radius: 14px; padding: 12px 14px; gap: 12px;
        box-shadow: 0 10px 28px -12px rgba(16,33,31,0.45);
      }
      .pwa-install-btn, .pwa-dismiss-btn { height: 40px; }
    }
    @media (max-width: 400px) {
      .pwa-banner { flex-direction: column; align-items: stretch; gap: 10px; }
      .pwa-actions { width: 100%; }
      .pwa-install-btn, .pwa-dismiss-btn { flex: 1; }
    }
  `]
})
export class PwaInstallComponent implements OnInit {
  showBanner = false;
  private deferredPrompt: any = null;

  ngOnInit(): void {
    // Don't show if user already dismissed recently
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      // Show again after 7 days
      if (Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000) return;
    }

    // Don't show if already installed (standalone mode)
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    // Listen for the browser's install prompt
    window.addEventListener('beforeinstallprompt', (e: Event) => {
      e.preventDefault();
      this.deferredPrompt = e;
      this.showBanner = true;
    });
  }

  async install(): Promise<void> {
    if (!this.deferredPrompt) return;
    this.deferredPrompt.prompt();
    const result = await this.deferredPrompt.userChoice;
    if (result.outcome === 'accepted') {
      this.showBanner = false;
    }
    this.deferredPrompt = null;
  }

  dismiss(): void {
    this.showBanner = false;
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  }
}
